import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { products } from '../data/products';
import { demoPages } from '../data/demoPages';
import { ProductCard } from '../components/home/ProductCard';
import { ProductPageModal } from '../components/home/ProductPageModal';
import { Button } from '../components/ui/button';
import { Sparkles, Loader2, AlertTriangle, ArrowRight, FolderHeart, Share2, Mail } from 'lucide-react';

const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedCollection = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/collection/${shareId}`);
        setData(res.data);
      } catch {
        setError(true);
      }
      setLoading(false);
    };
    fetch();
  }, [shareId]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: data?.name, text: data?.description, url }); return; } catch (err) { console.error('Native share failed:', err); }
    }
    try { await navigator.clipboard.writeText(url); } catch {
      const el = document.createElement('input'); el.value = url;
      document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el);
    }
  };

  const sharePinterest = () => {
    const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}&description=${encodeURIComponent(`${data?.name} — Curated collection from Create by Lumera`)}`;
    window.open(url, '_blank', 'width=600,height=500');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(`${data?.name || 'Collection'} — Create by Lumera`);
    const body = encodeURIComponent(`Hi!\n\nSjekk ut denne the collection: ${data?.name}\n${data?.description ? data.description + '\n' : ''}\n${window.location.href}\n\nMade with Create by Lumera`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center" data-testid="collection-error">
        <div className="text-center max-md px-6">
          <div className="w-16 h-16 rounded-full bg-[#F7F5F2] flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-[#6F6964]" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-[#111111] mb-2">Collectionen finnes ikke</h2>
          <p className="font-sans text-[#6F6964] mb-8">This collection is private or no longer exists.</p>
          <Button onClick={() => navigate('/')} className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-6 font-sans font-semibold">
            Utforsk Create by Lumera <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const collectionProducts = products.filter(p => data.product_titles.includes(p.title));

  return (
    <div className="min-h-screen bg-[#F7F5F2] dark:bg-background" data-testid="shared-collection-page">
      {/* Nav */}
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/lumera-logo.png" alt="Lumera" className="w-8 h-8 rounded-full" />
            <span className="text-lg font-serif font-semibold text-[#111111]">Lumera</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleShare} className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 font-sans text-xs text-stone-500 hover:text-primary hover:border-primary/30 transition-colors" data-testid="web-share-btn">
              <Share2 className="w-3.5 h-3.5" /> Kopier link
            </button>
            <button onClick={sharePinterest} className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 font-sans text-xs text-stone-500 hover:text-[#E60023] hover:border-[#E60023]/30 transition-colors" data-testid="pinterest-share-btn">
              <PinterestIcon className="w-3.5 h-3.5" /> Pinterest
            </button>
            <button onClick={shareEmail} className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-200 font-sans text-xs text-stone-500 hover:text-stone-700 hover:border-stone-400 transition-colors" data-testid="email-share-btn">
              <Mail className="w-3.5 h-3.5" /> Email
            </button>
            <Button onClick={() => navigate('/register')} data-testid="collection-cta" className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-5 h-8 text-xs font-sans font-semibold">
              Create your own
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-6">
        <div className="flex items-start gap-4 mb-2">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <FolderHeart className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">{data.name}</h1>
            {data.description && <p className="font-sans text-sm text-stone-500 mt-1">{data.description}</p>}
            <div className="flex items-center gap-2 mt-2">
              {data.creator?.avatar_url && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${data.creator.avatar_url}`} alt="" className="w-5 h-5 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <span className="font-sans text-xs text-stone-400">Kuratert av <span className="font-medium text-stone-600">{data.creator?.name}</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Products grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-10">
        {collectionProducts.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-sans text-stone-400">This collection is empty.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="collection-products-grid">
            {collectionProducts.map(product => (
              <ProductCard
                key={product.title}
                {...product}
                onClick={() => setSelectedProduct(product)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Branding footer */}
      <div className="bg-white border-t border-[#d8cfc8]/50 py-8 text-center" data-testid="collection-branding">
        <div className="flex items-center justify-center gap-2.5 mb-3">
          <img src="/lumera-logo.png" alt="Lumera" className="w-7 h-7 rounded-full" />
          <span className="font-serif font-semibold text-[#111111]">Made with Create by Lumera</span>
        </div>
        <p className="font-sans text-sm text-[#6F6964] mb-4">Create your own digitale products med AI-powered design.</p>
        <Button onClick={() => navigate('/')} variant="outline" className="rounded-full px-6 font-sans font-semibold border-[#d8cfc8] hover:border-[#B49A88]/30 hover:text-[#B49A88]">
          Utforsk markedsplassen <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Product modal */}
      {selectedProduct && demoPages[selectedProduct.title] && (
        <ProductPageModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onCreateProduct={() => navigate(`/generator?template=${encodeURIComponent(selectedProduct.title)}`)}
          onNextProduct={() => {
            const idx = collectionProducts.findIndex(p => p.title === selectedProduct.title);
            setSelectedProduct(collectionProducts[(idx + 1) % collectionProducts.length]);
          }}
          allProducts={collectionProducts}
        />
      )}
    </div>
  );
};

export default SharedCollection;
